import React, { useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Select,
  MenuItem,
  SelectChangeEvent,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DynamicInput from "./DynamicInput";
import IconTextButton from "components/shared/buttons/IconTextButton";
import { Add, Close, ContentPasteGoOutlined } from "@mui/icons-material";
import TierComponent from "./TierComponent";
import { SlotsFormValues, TierType } from "../types/types";
import { addSlots, updateSlots, updateTrips } from "services/createTripService";
import { toast } from "react-toastify";
import { getHostById } from "services/MerchantHostApi";
import { imageData } from "./ImageSection";
import { format, getHostAcceptance, TRIP_STATUS } from "utils/index";
import { useAppSelector } from "store";
import { ImageModal } from "components/index";
import { TripSlotImage, TripSlotTierImage } from "assets/index";
import Location from "./Location";
import StayDetails from "./StayDetailsSection";

interface SlotsProps {
  imageData?: imageData | null;
  editable?: boolean;
  tripId: string;
  merchantId: string;
  slots: SlotsFormValues[];
  setSlots: (slots: SlotsFormValues[]) => void;
  refetch: () => void;
  isHost?: boolean;
  tripDisplayId: string;
  tripDisplayTitle: string;
}

const Slots: React.FC<SlotsProps> = ({
  imageData,
  tripId,
  merchantId,
  slots,
  setSlots,
  editable = true,
  refetch,
  isHost = false,
  tripDisplayId,
  tripDisplayTitle
}) => {
  const userDetails = useAppSelector(
    (state) => state.appLayout.userDetails
  ) as any;
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number>(0);
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [tempHostId, setTempHostId] = useState<string>("");
  const [showExampleImage, setShowExampleImage] = useState(false);
  const [image, setImage] = useState(TripSlotImage);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>("APPROVED");

  const handleTripUpdate = async () => {
    const data: any = {
      slotCount: slots.length,
    };
    try {
      // check slot length and image data , update trip status accordingly and update slot length as well
      if (
        imageData?.cover?.fileName && // Cover image should exist
        imageData?.tripImages?.length > 0 // At least one trip image should exist
      ) {
        data.updatedAt = new Date().toISOString();
      }
      const response = await updateTrips(tripId, data);
      if (response.message === "Item updated successfully") {
        refetch();
      }
    } catch (error) {
      throw error;
    }
  };

  const handleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const handleTierChange = (slotIndex: number, updatedTiers: TierType[]) => {
    // **Step 1: Preserve the original tier data while tracking new changes**
    const newSlots = slots.map((slot, i) => {
      if (i === slotIndex) {
        return {
          ...slot,
          slotTiers: updatedTiers.map((tier) =>
            tier.isNew
              ? tier // ✅ Keep new tiers unchanged
              : {
                  ...tier,
                  newVersion: {
                    ...(tier.newVersion || tier), // Preserve previous data if already modified
                  },
                }
          ),
        };
      }
      return slot;
    });

    setSlots(newSlots);
  };

  const handleAddSlot = () => {
    if (slots.some((slot) => !slot.status)) {
      toast.warning("You can only submit one slot at a time.");
      return;
    }

    const newSlot: SlotsFormValues = {
      tripId,
      tripId_display: tripDisplayId,
      triptitle: tripDisplayTitle,
      merchantId,
      merchantName: userDetails?.organisationDetails?.brandName,
      hostId: "HOST202502000001",
      hostDetails: [
        {
          name: "Kozyclan",
          approvalStatus: "APPROVED",
          mobileNumber: "9711755071",
        },
      ],
      seats: "",
      slotTiers: [
        {
          id: 0,
          status: "ACTIVE",
          name: "",
          price: "",
          seatAllocation: "",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          moq:'1',
          isEdited: false,
          isNew: true,
          newVersion: null,
          initialSeats: "",
        },
      ],
      pickupLocation: [],
      dropLocation: [],
      startDate: "",
      endDate: "",
      startDateSearch: "",
      endDateSearch: "",
      slotApprovalStatus: "APPROVED",
      hostApprovalStatus: "CONFIRMED",
      initialTotalSeats: "",
      groupSize: ""
    };
    setSlots([...slots, newSlot]);
    setExpandedIndex(slots.length);
    setStatusFilter("APPROVED"); 
  };

  const handleSlotChange = (
    index: number,
    field: keyof SlotsFormValues,
    value: string
  ) => {
    const updatedSlots = [...slots];
    updatedSlots[index] = {
      ...updatedSlots[index],
      [field]: value,
    };
    setSlots(updatedSlots);
  };

  const handleSubmitSlot = async (index: number) => {
    const slot = slots[index];

    // **1️⃣ Validation checks**
    if (
      !slot.hostId ||
      !slot.seats ||
      slot.slotTiers.length === 0 ||
      slot.slotTiers.some(
        (tier) => !tier.name || !tier.price || !tier.seatAllocation
      ) ||
      slot.dropLocation?.length === 0 ||
      slot.pickupLocation?.length === 0
    ) {
      toast.warning("Please fill all required fields.");
      return;
    }
    
    // **2️⃣ Validate start & end dates**
    const startDate = new Date(slot.startDate);
    const endDate = new Date(slot.endDate);
    const currentTime = new Date();

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      toast.error("Invalid start or end date.");
      return;
    }

    if (startDate > endDate) {
      toast.error("End date cannot be earlier than start date.");
      return;
    }

    // **3️⃣ Ensure total allocated seats do not exceed the total seats**
    // const totalAllocatedSeats = slot.slotTiers
    //   .filter((tier) => tier.status === "ACTIVE")
    //   .reduce(
    //     (total, tier) =>
    //       total +
    //       (Number(tier.newVersion?.seatAllocation ?? tier.seatAllocation) || 0),
    //     0
    //   );

    // **🚨 Prevent updates if start time is within 2 hours**
    const diffInHours =
      (startDate.getTime() - currentTime.getTime()) / (1000 * 60 * 60);
    if (slot.status === "ACTIVE" && diffInHours < 2) {
      toast.error(
        "You cannot update a slot less than 2 hours before its start time."
      );
      return;
    }
    // if (totalAllocatedSeats!==-1 && totalAllocatedSeats !== Number(slot.seats)) {
    //   toast.warning(
    //     "Total allocated seats in tiers should be equal to the total seats."
    //   );
    //   return;
    // }

    setLoading(true);
    try {
      let response;
      if (slot.status === "ACTIVE") {
        const Slotid = slot.id as string;

        // **4️⃣ Categorize tiers**
        const newTiers = slot.slotTiers.filter((tier) => tier.isNew);
        const editedTiers = slot.slotTiers.filter((tier) => tier.isEdited);
        const unchangedTiers = slot.slotTiers.filter(
          (tier) => !tier.isEdited && !tier.isNew
        );

        // **5️⃣ Handle Edited Tiers**
        const inactiveOldTiers = editedTiers.map((tier) => ({
          ...tier,
          status: "INACTIVE",
          isEdited: false,
        }));

        // **Find the highest existing ID to avoid duplicate IDs**
        const maxExistingId = Math.max(
          ...slot.slotTiers.map((tier) => tier.id),
          0
        );
        let nextId = maxExistingId + 1;

        const replicatedEditedTiers = editedTiers.map((tier, i) => ({
          ...tier,
          ...(tier.newVersion ?? tier), // ✅ Use `newVersion` to apply modifications
          id: nextId++, // Assign new ID
          status: "ACTIVE",
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          isEdited: false,
          isNew: false,
        }));

        // **6️⃣ Construct the final tier list (Remove `newVersion` before sending)**
        const finalTiers = [
          ...unchangedTiers.map((tier) => ({ ...tier, newVersion: null })), // ✅ Remove `newVersion`
          ...inactiveOldTiers.map((tier) => ({
            ...tier,
            newVersion: null,
          })), // ✅ Remove `newVersion`
          ...replicatedEditedTiers.map((tier) => ({
            ...tier,
            newVersion: null,
          })), // ✅ Remove `newVersion`
          ...newTiers.map((tier) => ({
            ...tier,
            isNew: false,
            newVersion: null,
            initialSeats: tier.seatAllocation,
          })), // ✅ Remove `newVersion`
        ];

        // **7️⃣ Prepare the final API payload**
        const updateField = {
          seats: slot.seats,
          slotTiers: finalTiers, // ✅ Ensures correct tier state (active, inactive, new, edited)
          startDate: slot.startDate,
          endDate: slot.endDate,
          hostId: slot.hostId,
          hostDetails: slot.hostDetails,
          dropLocation: slot.dropLocation,
          pickupLocation: slot.pickupLocation,
          slotApprovalStatus: "APPROVED",
          groupSize: slot.groupSize
        };

        response = (await updateSlots(Slotid, updateField)) as any;
        toast.success("Slot updated successfully");

        // **8️⃣ Update state**
        slots[index] = response?.data?.updatedItem;
      } else {
        // **Create a new slot**
        const updatedTiers = slot.slotTiers.map((tier) => ({
          ...tier,
          isNew: false,
          initialSeats: tier.seatAllocation,
        }));
        slot.initialTotalSeats = slot.seats;
        slot.slotTiers = updatedTiers;

        response = await addSlots(slot);
        toast.success("Slot added successfully");

        slots[index] = response?.data?.document;

        // Update trip status
        await handleTripUpdate();
      }

      setExpandedIndex(null);
      setTempHostId("");
    } catch (error) {
      console.error("Error saving slot", error);
      toast.error("Failed to save slot");
    } finally {
      setLoading(false);
      refetch();
    }
  };

  const handleAddHost = async (index: number, id: string) => {
    if (!id || id.length < 11) {
      toast.error("Invalid Host ID format");
      return null;
    }
    // if host id already added then don't alow to add maximum 1 host
    if (slots[index].hostId !== "") {
      toast.error("Maximum 1 host can be added");
      return;
    }

    // Prevent adding duplicate IDs
    if (slots[index]?.hostId === id) {
      toast.warning("Host ID already added");
      return;
    }

    const newHost = await getHostById(id);
    if (newHost && newHost?.approvalStatus !== "APPROVED") {
      toast.error("Host is not approved");
      return;
    }
    if (newHost) {
      const updatedSlots = [...slots];
      updatedSlots[index].hostId = id;
      updatedSlots[index].hostDetails.push(newHost);
      setSlots(updatedSlots);
    }
  };

  const handleRemoveHost = (index: number) => {
    const updatedSlots = [...slots];
    updatedSlots[index].hostId = "";
    updatedSlots[index].hostDetails = [];
    setSlots(updatedSlots);
  };

  const handleModalClick = (imageName: string) => {
    if (imageName === "slot") {
      setImage(TripSlotImage);
    } else if (imageName === "tier") {
      setImage(TripSlotTierImage);
    }
    setShowExampleImage(true);
  };

  const handleCloseBooking = async (index: number) => {
    const slot = slots[index];
    setLoading(true);
    try {
      const updateField = {
        slotApprovalStatus: "CLOSED",
      };
      const Slotid = slot.id as string;
      await updateSlots(Slotid, updateField);
      toast.success("Slot updated successfully");
    } catch (error) {
    } finally {
      setLoading(false);
      refetch();
    }
  };

  //functionality to create a clone of slot
  const handleCloneSlot = () => {
    const sourceSlot = filteredSlots[selectedSlotIndex];
    if (!sourceSlot) {
      toast.warning("Please select a valid slot to duplicate");
      return;
    }

    const clonedTiers = sourceSlot.slotTiers
      .filter((tier) => tier.status === "ACTIVE")
      .map((tier, index) => ({
        ...tier,
        id: index,
        status: "ACTIVE",
        isNew: true,
        isEdited: false,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        newVersion: null,
      }));

    // Create a new slot with cloned data
    //we are not spreading as it will add slot.status and other create ,updated which are added from backend
    const clonedSlot: SlotsFormValues = {
      tripId: sourceSlot.tripId,
      merchantId: sourceSlot.merchantId,
      merchantName: userDetails?.organisationDetails?.brandName,
      tripId_display: sourceSlot?.tripId_display,
      triptitle: sourceSlot?.triptitle,
      hostId: sourceSlot.hostId,
      hostDetails: sourceSlot.hostDetails,
      seats: sourceSlot.seats,
      slotTiers: clonedTiers as TierType[],
      pickupLocation: sourceSlot.pickupLocation as [],
      dropLocation: sourceSlot.dropLocation as [],
      startDate: sourceSlot.startDate,
      endDate: sourceSlot.endDate,
      startDateSearch: sourceSlot.startDateSearch,
      endDateSearch: sourceSlot.endDateSearch,
      slotApprovalStatus: "APPROVED",
      hostApprovalStatus: "CONFIRMED",
      groupSize: sourceSlot.groupSize
    };

    // Add the cloned slot to the slots array
    setSlots([...slots, clonedSlot]);

    // Expand the newly added slot
    setExpandedIndex(slots.length);
    setStatusFilter("APPROVED"); 
  };

  const handleSlotSelection = (event: SelectChangeEvent<number>) => {
    setSelectedSlotIndex(event.target.value as number);
  };

  const handleStatusFilterChange = (
    _: React.MouseEvent<HTMLElement>,
    newFilter: string
  ) => {
    if (newFilter !== null) {
      setStatusFilter(newFilter.toUpperCase());
      setExpandedIndex(null);
      // Reset clone selection so it can't point outside the newly filtered slots.
      setSelectedSlotIndex(0);
    }
  };

  const filteredSlots = slots?.filter((slot) => {
    if (statusFilter === "APPROVED") return slot.slotApprovalStatus === "APPROVED";
    if (statusFilter === "CLOSED") return slot.slotApprovalStatus === "CLOSED";
    if (statusFilter === "OTHER") return slot.slotApprovalStatus !== "APPROVED" && slot.slotApprovalStatus !== "CLOSED";
    return true;
  }).sort((a, b) => {
    const dateA = a?.startDate ? new Date(a?.startDate) : new Date(0);
    const dateB = b?.startDate ? new Date(b?.startDate) : new Date(0);
    return dateB.getTime() - dateA.getTime();
  });

  return (
    <Card sx={{ marginTop: 3, padding: { xs: 1, sm: 2 }, width: "100%" }}>
      <CardContent>
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Typography
            fontWeight={600}
            variant="h6"
            color="#181D27"
            marginBottom={1}
            display={isHost ? "none" : "flex"}
          >
            Trip Slots
          </Typography>
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            gap={1}
            mt={{ xs: 1, sm: 0 }}
            ml={{ xs: 0, sm: "auto" }}
          >
            <IconTextButton
              label="See Slot Sample"
              onClick={() => handleModalClick("slot")}
              sx={{ cursor: "pointer" }}
            />
            <IconTextButton
              label="See Slot Tier Sample"
              onClick={() => handleModalClick("tier")}
              sx={{ cursor: "pointer" }}
            />
          </Box>
        </Box>
        <Typography
          variant="body2"
          color="#535862"
          marginBottom={3}
          display={isHost ? "none" : "flex"}
          fontFamily="Inter"
          fontSize={14}
          fontWeight={400}
          width={{ xs: "100%", sm: "70%" }}
        >
          Use this section to create a trip slot by selecting the start and end
          dates, adding at least one pickup and drop location, and setting up
          price tiers (or a single price, named as per your preference).
        </Typography>

          <Box
            sx={{
              marginBottom: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="body2" color="#535862" marginBottom={1}>
              Filter by Status:
            </Typography>
            <ToggleButtonGroup
              value={statusFilter}
              exclusive
              onChange={handleStatusFilterChange}
              aria-label="slot status filter"
              size="small"
              sx={{
                "& .MuiToggleButton-root": {
                  borderRadius: "8px",
                  textTransform: "none",
                  fontSize: "12px",
                  padding: "6px 12px",
                  margin: "2px",
                  "&.Mui-selected": {
                    backgroundColor: "#1976d2",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#1565c0",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="APPROVED" aria-label="approved slots">
                Approved
              </ToggleButton>
              <ToggleButton value="CLOSED" aria-label="closed slots">
                Closed
              </ToggleButton>
              <ToggleButton value="OTHER" aria-label="other slots">
                Others
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

        {slots.length === 0 && (
          <Typography
            fontFamily="Inter"
            variant="body2"
            color="#535862"
            marginBottom={3}
          >
            No slots available create a new slot.
          </Typography>
        )}
        {filteredSlots?.length === 0 && slots.length > 0 && (
          <Typography
            fontFamily="Inter"
            variant="body2"
            color="#535862"
            marginBottom={3}
          >
            No slots found for the selected filter.
          </Typography>
        )}
        {filteredSlots?.map((section, index) => {
          const actualIndex = slots.findIndex((slot) => slot === section);
          return (
            <Accordion
              key={actualIndex}
              expanded={expandedIndex === actualIndex}
              onChange={() => handleExpand(actualIndex)}
              sx={{
                marginY: 2,
                "&:before": { display: "none" },
                border: "1px solid #D5D7DA",
                borderRadius: "8px !important",
                boxShadow: "none",
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    flexWrap: "wrap",
                    width: "100%",
                  }}
                >
                  <Typography
                    variant="body1"
                    color="#181D27"
                    fontWeight={500}
                    fontFamily="Inter, sans-serif"
                    fontSize="16px"
                    sx={{ flexShrink: 0 }}
                  >
                    Slot {index + 1} :
                  </Typography>
                  {section?.searchIdKey && (
                    <Typography
                      variant="body2"
                      color="textSecondary"
                      fontWeight={500}
                      fontFamily="Inter, sans-serif"
                      fontSize={{ xs: "14px", sm: "16px" }}
                      sx={{ flexShrink: 0, ml: 1 }}
                    >
                      {(section?.searchIdKey as string) + section?.searchId}
                    </Typography>
                  )}
                  {section.startDate &&
                    section.endDate &&
                    actualIndex !== expandedIndex && (
                      <Typography
                        variant="body2"
                        color="#535862"
                        fontFamily="Inter"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: { xs: "normal", sm: "nowrap" },
                          marginLeft: 2,
                          maxWidth: { xs: "100%", sm: "60%" },
                          width: { xs: "100%", sm: "auto" },
                          mt: { xs: 1, sm: 0 },
                        }}
                      >
                        {format(section?.startDate)} -{" "}
                        {format(section?.endDate)}
                      </Typography>
                    )}
                  {section?.slotApprovalStatus && (
                    <Chip
                      label={`Slot Status - ${section?.slotApprovalStatus}`}
                      color="primary"
                      sx={{
                        marginLeft: { xs: 0, sm: "auto" },
                        marginTop: { xs: 1, sm: 0 },
                        marginRight: { xs: 0, sm: 2 },
                        fontSize: "14px",
                        width: { xs: "fit-content", sm: "auto" },
                      }}
                    />
                  )}
                </Box>
              </AccordionSummary>

              <AccordionDetails
                sx={{ padding: { xs: 1, sm: 2 }, alignItems: "center" }}
              >
                {/* Heading Input */}
                <Box flexBasis="100%" mb={4}>
                  <Box display="none" flexWrap="wrap" gap={1} mt={1}>
                    <DynamicInput
                      name="hostId"
                      type="text"
                      label="Assign Host*"
                      placeholder="Enter Host ID"
                      value={tempHostId}
                      onChange={(e) => setTempHostId(e.target.value)}
                      disabled={
                        !editable || section?.slotApprovalStatus === "CLOSED"
                      }
                      adornment={
                        <IconTextButton
                          label="Add"
                          onClick={() => handleAddHost(index, tempHostId)}
                          disabled={
                            !tempHostId ||
                            section?.slotApprovalStatus === "CLOSED"
                          }
                        />
                      }
                    />
                    {Array.isArray(section?.hostDetails) &&
                      section?.hostDetails?.map((host, idx) => (
                        <Chip
                          key={idx}
                          label={`Host Name:- ${host.name} | Approval Status:- (${host.approvalStatus}) | Host Mobile No. :- (${host.mobileNumber})`}
                          onDelete={() => handleRemoveHost(index)}
                          sx={{
                            background: "#E0F2F1",
                            fontSize: "14px",
                            maxWidth: "100%",
                            height: "auto",
                            "& .MuiChip-label": {
                              whiteSpace: "normal",
                              overflow: "visible",
                              padding: "8px 4px",
                            },
                          }}
                          disabled={
                            !editable ||
                            section?.slotApprovalStatus === "CLOSED"
                          }
                        />
                      ))}
                  </Box>
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                  >
                    <DynamicInput
                      name="seats"
                      type="number"
                      label="No. of Total Seats"
                      placeholder="Enter No. of Seats"
                      required
                      value={section.seats}
                      onChange={(e) =>
                        handleSlotChange(actualIndex, "seats", e.target.value)
                      }
                      disabled={
                        !editable || section?.slotApprovalStatus === "CLOSED"
                      }
                    />
                    <DynamicInput
                      name="groupSize"
                      type="number"
                      label="Group Size"
                      placeholder="Enter Group Size"
                      required
                      value={section.groupSize}
                      onChange={(e) =>
                        handleSlotChange(actualIndex, "groupSize", e.target.value)
                      }
                      disabled={
                        !editable || section?.slotApprovalStatus === "CLOSED"
                      }
                    />
                    {false && section?.hostId !== "" && section?.status && (
                      <Chip
                        label={`Host Acceptance: ${section?.hostApprovalStatus}`}
                        sx={{
                          background: getHostAcceptance(
                            section?.hostApprovalStatus
                          ).bgcolor,
                          fontFamily: "inter",
                          fontSize: { xs: "16px", sm: "20px" },
                          mt: { xs: 2, sm: "auto" },
                          mb: 1,
                          marginInline: "auto",
                          maxWidth: "100%",
                          height: "auto",
                          "& .MuiChip-label": {
                            whiteSpace: "normal",
                            padding: "4px",
                          },
                        }}
                      />
                    )}
                  </Box>
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    gap={2}
                    marginTop={2}
                  >
                    <DynamicInput
                      name="startDate"
                      type="date"
                      placeholder="Select Start Date"
                      required
                      value={section.startDate}
                      onChange={(e) => {
                        const value = new Date(e.target.value).toISOString();
                        const date = new Date(value)
                          .toLocaleDateString("en-GB", {
                            month: "2-digit",
                            year: "numeric",
                          })
                          .replace(/\//g, "-");

                        const updatedSlots = [...slots];
                        updatedSlots[actualIndex] = {
                          ...updatedSlots[actualIndex],
                          startDate: value,
                          startDateSearch: date,
                        };
                        setSlots(updatedSlots);
                      }}
                      disabled={
                        !editable ||
                        section?.status === "ACTIVE" ||
                        section?.slotApprovalStatus === "CLOSED"
                      }
                    />
                    <DynamicInput
                      name="endDate"
                      type="date"
                      placeholder="Select End Date"
                      required
                      value={section.endDate}
                      onChange={(e) => {
                        const value = new Date(e.target.value).toISOString();
                        const date = new Date(value)
                          .toLocaleDateString("en-GB", {
                            month: "2-digit",
                            year: "numeric",
                          })
                          .replace(/\//g, "-");

                        const updatedSlots = [...slots];
                        updatedSlots[actualIndex] = {
                          ...updatedSlots[actualIndex],
                          endDate: value,
                          endDateSearch: date,
                        };
                        setSlots(updatedSlots);
                      }}
                      disabled={
                        !editable ||
                        section?.status === "ACTIVE" ||
                        section?.slotApprovalStatus === "CLOSED"
                      }
                    />
                  </Box>
                  <Location
                    dropLocation={section?.dropLocation}
                    pickupLocation={section?.pickupLocation}
                    onChange={(field, value) =>
                      handleSlotChange(actualIndex, field, value)
                    }
                    editable={
                      editable && section?.slotApprovalStatus !== "CLOSED"
                    }
                  />

                  <TierComponent
                    slotIndex={actualIndex}
                    tiers={section.slotTiers}
                    onChange={handleTierChange}
                    editable={
                      editable && section?.slotApprovalStatus !== "CLOSED"
                    }
                  />
                </Box>
                {slots[actualIndex].slotApprovalStatus !== "CLOSED" && (
                  <Box
                    display="flex"
                    flexDirection={{ xs: "column", sm: "row" }}
                    justifyContent="space-between"
                    gap={2}
                    width="100%"
                  >
                    {slots[actualIndex].status && (
                      <IconTextButton
                        label="Close Booking"
                        onClick={() => handleCloseBooking(actualIndex)}
                        sx={{
                          display: isHost ? "none" : "block",
                          width: { xs: "100%", sm: "auto" },
                        }}
                        disabled={!editable || loading}
                      />
                    )}
                    <IconTextButton
                      label={
                        slots[actualIndex].status ? "Update Slot" : "Create Slot"
                      }
                      onClick={() => handleSubmitSlot(actualIndex)}
                      sx={{
                        display: isHost ? "none" : "block",
                        marginLeft: { xs: 0, sm: "auto" },
                        width: { xs: "100%", sm: "auto" },
                        marginTop: { xs: 1, sm: 0 },
                      }}
                      disabled={!editable || loading}
                    />
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )
        })}
        {!slots?.some((slot) => !slot.status) && (
          <Box
            sx={{
              justifyContent: "space-between",
              display: isHost ? "none" : "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              marginTop: "20px",
            }}
          >
            {Array.isArray(slots) && slots?.length !== 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  width: { xs: "100%", sm: "auto" },
                }}
              >
                <Select
                  value={selectedSlotIndex}
                  onChange={handleSlotSelection}
                  displayEmpty
                  disabled={!editable || filteredSlots.length === 0}
                  sx={{
                    minWidth: 50,
                    maxHeight: 40,
                    width: { xs: "100%", sm: "auto" },
                  }}
                >
                  {filteredSlots.map((slot, index) => (
                    <MenuItem key={slot.id} value={index}>
                      Slot {index + 1}
                    </MenuItem>
                  ))}
                </Select>
                <IconTextButton
                  Icon={<ContentPasteGoOutlined />}
                  label="Duplicate slot"
                  onClick={handleCloneSlot}
                  disabled={!editable || filteredSlots.length === 0}
                  sx={{ width: { xs: "100%", sm: "auto" } }}
                />
              </Box>
            )}
            <IconTextButton
              Icon={<Add />}
              label="Add new slots"
              onClick={handleAddSlot}
              sx={{
                marginLeft: { xs: 0, sm: "auto" },
                width: { xs: "100%", sm: "auto" },
              }}
              disabled={!editable}
            />
          </Box>
        )}
      </CardContent>
      <ImageModal
        open={showExampleImage}
        onClose={() => setShowExampleImage(false)}
      >
        <img
          src={image}
          alt="See Dummy Image"
          style={{
            maxWidth: "100%",
            height: "auto",
            borderRadius: 8,
          }}
        />
      </ImageModal>
    </Card>
  );
};

export default Slots;
